import { FC, useMemo, useRef, useCallback } from 'react';
// 组件
import { Table } from 'antd';
import { ProForm } from '../antd';
import { PlusOutlined, } from '@ant-design/icons';
import TotalTable, {
  TotalTableActionType,
} from '../EdiTableCopy/subpage/totalTable';
import EditVirtualTableTable from './subpage/editVirtualTableTable';
import EditVirtualTableWrapper from './subpage/editVirtualTableWrapper';
import EditVirtualTableCell from './subpage/editVirtualTableCell';
import CopyTableData from '../CopyTableData';
import EditVirtualTableContext from './context/editVirtualTableContext';
// 方法
import {
  formtaFormItemName,
  formatDataSource,
  dataSourceToFormData,
  getRowformData,
  formatUploadDataSource,
  getColumnFieldNames,
} from './utils';
import useEditVirtualTableContext from './hooks/useEditVirtualTableContext';
import useVirtualTableColumns from './hooks/virtualTableColumns';
import { EditVirtualTableProps } from './interface';
import { filterDataSource } from './utils';
// 常量
import { STATUS_CREATED_CODE, STATUS_CREATED } from '../constants/constant';
import './style.less';
/** 可编辑虚拟列表 */
const EditVirtualTable: FC<EditVirtualTableProps> = props => {
  const {
    actionRef,
    height = 350,
    sort,
    sortFieldName,
    mergeRowKey,
    rowClassName,
    totalKeys,
    bordered,
    totalIndex,
    totalText,
    onValuesChange,
    columns: columnsProps,
    dataSource: dataSourceProps,
    copyTableDataProps,
    ...restProps
  } = props;
  const [form] = ProForm.useForm();
  const flagIndex = useRef<number>(0); // 末尾行key指针
  const prevChangeName = useRef<string>(); // 上一次改变的表单name
  const timeOutFlag = useRef<any>(); // 定时器
  const totalTableRef = useRef<TotalTableActionType>(); // 统计组件
  const currentDataSource = useMemo(() => {
    return formatUploadDataSource(dataSourceProps, columnsProps);
  }, [columnsProps, dataSourceProps]);
  const { dataSource, loading, dispatchEditVirtualTable } = useEditVirtualTableContext(
    flagIndex,
    currentDataSource,
    form
  );
  const { columns, selecTableInfo } = useVirtualTableColumns(
    props,
    dispatchEditVirtualTable,
    form,
  );
  // 提交 校验问题待优化
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      return formatDataSource(dataSource, sort, sortFieldName);
    } catch (errorInfo: any) {
      return false;
    }
  };
  // 获取当前可编辑表的值
  const handleDataSource = (filterDeleteData?: boolean) => {
    if (filterDeleteData) {
      return dataSource?.filter(item => item[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE) ?? [];
    }
    return dataSource || [];
  };
  // 表单值发生改变
  const handleValuesChange = (changedValues: any, values: any) => {
    const formItemName = Object.keys(changedValues)?.[0];
    const formItemValue = Object.values(changedValues)?.[0];
    const { name, key } = formtaFormItemName(formItemName);
    if (name !== undefined && key !== undefined) {
      if (name === prevChangeName.current) {
        clearTimeout(timeOutFlag.current);
      }
      prevChangeName.current = name;
      timeOutFlag.current = setTimeout(() => {
        const changeVals = { [name]: formItemValue };
        if (selecTableInfo[name]) {
          const labelName = `${selecTableInfo[name]}-${key}`;
          const fieldsValue = form.getFieldsValue();
          changeVals[selecTableInfo[name]] = fieldsValue[labelName];
        }
        dispatchEditVirtualTable('change', {
          changeItem: changeVals,
          rowKey: key,
        });
        const rowValues = getRowformData(values, key);
        const fintItem = dataSource?.find(item => item.key === key);
        const findIndex = dataSource?.findIndex(item => item.key === key);
        onValuesChange?.(
          formItemValue,
          changeVals,
          { ...fintItem, ...rowValues },
          findIndex !== undefined ? findIndex : -1,
          form,
        );
      }, 100);
    }
  };
  // 添加可编辑表数据
  const handleAdd = useCallback(
    (addItem?: any, isEmpty?: boolean) => {
      dispatchEditVirtualTable('addRow', { addItem, isEmpty });
    },
    [dispatchEditVirtualTable],
  );
  /** 更新dataSource */
  const handleChangeDataSource = (newDataSource?: any[]) => {
    const formData = dataSourceToFormData(columns, newDataSource);
    form.setFieldsValue(formData);
    dispatchEditVirtualTable('changeDataSource', {
      updateDataSource: newDataSource || [],
    });
  };
  // 删除
  const handleDelete = (record: any) => {
    const recordKey = record?.key;
    dispatchEditVirtualTable('deleteRow', { rowKey: recordKey });
  };
  /** 清空数据 */
  const handleEmpty = () => {
    dispatchEditVirtualTable('changeDataSource', { updateDataSource: [] });
    form.resetFields();
  };
  /** 批量修改 */
  const handleBatchChange = (dataIndex: string, value: any) => {
    if (!dataSource || !dataSource.length) return;
    const newDataSource = dataSource.map(item => {
      if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE) {
        if (item[dataIndex] === value) return item;
        return {
          ...item,
          [dataIndex]: value,
          [STATUS_CREATED_CODE]:
            item[STATUS_CREATED_CODE] === STATUS_CREATED.ADD
              ? STATUS_CREATED.ADD
              : STATUS_CREATED.UPDATE,
        };
      }
      return item;
    });
    handleChangeDataSource(newDataSource);
  };
  /** 覆盖 */
  const handleCover = (coverItem?: any[]) => {
    dispatchEditVirtualTable('cover', { coverItem });
  };
  /** 追加 */
  const handleAppend = (appendItem?: any[]) => {
    dispatchEditVirtualTable('append', { appendItem, fieldNames: getColumnFieldNames(columns), });
  };
  /** 自定义表格组件 */
  const components = useMemo(() => {
    return {
      body: {
        wrapper: EditVirtualTableWrapper,
        cell: EditVirtualTableCell,
      },
      table: EditVirtualTableTable,
    };
  }, [height]);
  const handleRowClassName = (record: any, index: number, indent: number) => {
    if (record.isMainTable) return 'virtual-table-mian-row';
    if (rowClassName) {
      if (typeof rowClassName === 'string') return rowClassName;
      return rowClassName(record, index, indent);
    }
    return '';
  };
  const pageDataSource = useMemo(() => {
    return filterDataSource(dataSource, mergeRowKey);
  }, [dataSource, mergeRowKey]);
  if (actionRef) {
    actionRef.current = {
      getDataSource: handleDataSource,
      submit: handleSubmit,
      add: handleAdd,
      delete: handleDelete,
      changeDataSource: handleChangeDataSource,
      empty: handleEmpty,
      batchChange: handleBatchChange,
      cover: handleCover,
      append: handleAppend,
      form: form,
    };
  }

  return (
    <EditVirtualTableContext.Provider
      value={{
        form,
        dataSource,
        dispatchEditVirtualTable,
      }}
    >
      <div className='edit-virtual-table-wrapper'>  
        <ProForm
          form={form}
          component={false}
          onValuesChange={handleValuesChange}
        >
          <Table
            className="edit-virtual-table"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={pageDataSource}
            pagination={false}
            components={components}
            scroll={{ y: height }}
            rowClassName={handleRowClassName}
            bordered={bordered}
            summary={() => {
              if (
                Array.isArray(totalKeys) &&
                !!totalKeys.length &&
                Array.isArray(pageDataSource) &&
                !!pageDataSource.length
              ) {
                return (
                  <Table.Summary fixed>
                    <TotalTable
                      actionRef={totalTableRef}
                      columns={columns}
                      totalKeys={totalKeys}
                      dataSource={pageDataSource}
                      totalIndex={totalIndex}
                      totalText={totalText}
                    />
                  </Table.Summary>
                );
              }
            }}
            {...restProps}
          />
        </ProForm>
        {
          !!copyTableDataProps && (
            <div className='edit-virtual-table-copy'>
              <CopyTableData
                dataSource={pageDataSource}
                {...copyTableDataProps}
              />
            </div>
          )
        }
      </div>
    </EditVirtualTableContext.Provider>
  );
};

EditVirtualTable.defaultProps = {
  disabled: false,
  hideAdd: false,
  addIcon: <PlusOutlined />,
  sort: false,
  sortFieldName: 'displayOrder',
  height: 350,
  bordered: true,
  operationWidth: 50,
};

export default EditVirtualTable;
